/**
 * Public Learning Path Play Page
 * Displays a published A4 learning path and opens clicked element content in an in-page drawer.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Drawer, Dropdown, Empty, Space, Spin } from 'antd';
import { ArrowsAltOutlined, CloseOutlined, FilePdfOutlined, FileTextOutlined, FilterOutlined, FormOutlined, LinkOutlined, PlayCircleOutlined, QuestionCircleOutlined, SoundOutlined, ToolOutlined } from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import LIFELogo from '../Home/LIFE_logo.png';
import { getFileUrl } from '../../utils/urlUtils';
import {
  endLearningPathSession,
  getPublicLearningPath,
  recordLearningPathElementInteraction,
  recordLearningPathSessionEvent,
  recordLearningPathVisit,
  startLearningPathSession,
} from '../../services/learningPathsService';
import styles from './LearningPaths.module.css';

const createLearningPathSessionKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getDeviceType = () => {
  if (typeof window === 'undefined') {
    return 'desktop';
  }

  const width = window.innerWidth || 0;
  if (width <= 768) {
    return 'mobile';
  }

  if (width <= 1024) {
    return 'tablet';
  }

  return 'desktop';
};

const interactionTypeToResourceType = {
  'pdf-open': 'pdf',
  'audio-open': 'audio',
  'video-open': 'video',
  'link-open': 'link',
  'quiz-open': 'quiz',
  'survey-open': 'survey',
  'training-profile-open': 'training-profile',
};

/**
 * Public learner-facing Learning Path playback page.
 * @returns {JSX.Element} Learning Path viewer
 */
const LearningPathPlay = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const minZoom = 0.4;
  const maxZoom = 1;
  const [path, setPath] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [activeFilters, setActiveFilters] = useState([]);
  const [minimizedMedia, setMinimizedMedia] = useState(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const minimizedAudioRef = useRef(null);
  const minimizedVideoRef = useRef(null);
  const loggedMediaInteractionsRef = useRef({});
  const pendingRestoreRef = useRef(null);
  const sessionIdRef = useRef(null);
  const sessionKeyRef = useRef(createLearningPathSessionKey());
  const sequenceIndexRef = useRef(0);
  const lastOpenedElementIdRef = useRef(null);
  const openElementRef = useRef(null);
  const pendingExternalResourceRef = useRef(null);
  const activeMediaRef = useRef(null);
  const mediaTransferRef = useRef(false);
  const selectedElementRef = useRef(null);
  const playPageRef = useRef(null);

  const contentFilters = useMemo(() => [
    {
      key: 'rich-text',
      icon: <FileTextOutlined />,
      label: t('learningPathPages.elementContent.info', 'Info'),
      matches: (element) => Boolean(element.richTextContentInfo),
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined />,
      label: t('learningPathPages.elementContent.pdf', 'PDF'),
      matches: (element) => Boolean(element.pdf),
    },
    {
      key: 'video',
      icon: <PlayCircleOutlined />,
      label: t('learningPathPages.elementContent.video', 'Video'),
      matches: (element) => Boolean(element.video),
    },
    {
      key: 'audio',
      icon: <SoundOutlined />,
      label: t('learningPathPages.elementContent.audio', 'Audio'),
      matches: (element) => Boolean(element.audio),
    },
    {
      key: 'link',
      icon: <LinkOutlined />,
      label: t('learningPathPages.elementContent.link', 'Link'),
      matches: (element) => Boolean(element.linkUrl),
    },
    {
      key: 'quiz',
      icon: <QuestionCircleOutlined />,
      label: t('learningPathPages.elementContent.quiz', 'Quiz'),
      matches: (element) => Boolean(element.quizCollectionId),
    },
    {
      key: 'survey',
      icon: <FormOutlined />,
      label: t('learningPathPages.elementContent.survey', 'Survey'),
      matches: (element) => Boolean(element.surveyId),
    },
    {
      key: 'training-profile',
      icon: <ToolOutlined />,
      label: t('learningPathPages.elementContent.trainingProfile', 'Training profile'),
      matches: (element) => Boolean(element.trainingProfileId),
    },
  ], [t]);

  const elementActions = useMemo(() => [
    {
      key: 'pdf',
      field: 'pdf',
      icon: <FilePdfOutlined />,
      label: t('learningPathPages.elementContent.pdf', 'PDF'),
      getHref: (element) => `/api/files/${element.pdf.id}/download`,
      getText: (element) => element.pdf.fileName,
      interactionType: 'pdf-open',
    },
    {
      key: 'link',
      field: 'linkUrl',
      icon: <LinkOutlined />,
      label: t('learningPathPages.elementContent.link', 'Link'),
      getHref: (element) => element.linkUrl,
      getText: (element) => element.linkUrl,
      interactionType: 'link-open',
    },
    {
      key: 'quiz',
      field: 'quizCollectionId',
      icon: <QuestionCircleOutlined />,
      label: t('learningPathPages.elementContent.quiz', 'Quiz'),
      getHref: (element) => `/quiz/${element.quizCollectionId}`,
      getText: (element) => element.quizCollectionName || String(element.quizCollectionId),
      interactionType: 'quiz-open',
    },
    {
      key: 'survey',
      field: 'surveyId',
      icon: <FormOutlined />,
      label: t('learningPathPages.elementContent.survey', 'Survey'),
      getHref: (element) => `/survey/${element.surveyId}`,
      getText: (element) => element.surveyName || String(element.surveyId),
      interactionType: 'survey-open',
    },
    {
      key: 'training-profile',
      field: 'trainingProfileId',
      icon: <ToolOutlined />,
      label: t('learningPathPages.elementContent.trainingProfile', 'Training profile'),
      getHref: (element) => `/simulations/heat-pump-training?profileId=${element.trainingProfileId}`,
      getText: (element) => element.trainingProfileName || String(element.trainingProfileId),
      interactionType: 'training-profile-open',
    },
  ], [t]);

  const getElementActionItems = useCallback((element) => {
    if (!element) {
      return [];
    }

    return elementActions.filter((action) => Boolean(element[action.field]));
  }, [elementActions]);

  const getDirectOpenAction = useCallback((element) => {
    if (!element || element.richTextContentInfo || element.audio || element.video) {
      return null;
    }

    const availableActions = getElementActionItems(element);
    return availableActions.length === 1 ? availableActions[0] : null;
  }, [getElementActionItems]);

  const getElementButtonLabel = useCallback((element) => {
    const directOpenAction = getDirectOpenAction(element);
    if (directOpenAction) {
      return t('learningPathPages.play.openElementResourceNewTab', 'Open {{resource}} for {{name}} in a new tab', {
        resource: directOpenAction.label,
        name: element.name,
      });
    }

    return t('learningPathPages.play.openElement', 'Open {{name}}', {
      name: element.name,
    });
  }, [getDirectOpenAction, t]);

  const loadPath = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      sessionIdRef.current = null;
      sessionKeyRef.current = createLearningPathSessionKey();
      sequenceIndexRef.current = 0;
      lastOpenedElementIdRef.current = null;
      openElementRef.current = null;
      pendingExternalResourceRef.current = null;
      activeMediaRef.current = null;
      mediaTransferRef.current = false;
      const result = await getPublicLearningPath(id);
      setPath(result);
      recordLearningPathVisit(id).catch(() => {});
      startLearningPathSession(id, {
        sessionKey: sessionKeyRef.current,
        entryPath: typeof window !== 'undefined' ? window.location.pathname + window.location.search : `/learning-path/${id}`,
        deviceType: getDeviceType(),
        viewportWidth: typeof window !== 'undefined' ? window.innerWidth : null,
        viewportHeight: typeof window !== 'undefined' ? window.innerHeight : null,
        language: typeof document !== 'undefined' ? document.documentElement.lang || undefined : undefined,
      }).then((session) => {
        sessionIdRef.current = session?.sessionId || null;
      }).catch(() => {});
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPath();
  }, [loadPath]);

  useEffect(() => {
    loggedMediaInteractionsRef.current = {};
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [selectedElement?.id]);

  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  const filterCounts = useMemo(() => {
    const counts = {};

    contentFilters.forEach((filter) => {
      counts[filter.key] = (path?.elements || []).filter((element) => filter.matches(element)).length;
    });

    return counts;
  }, [contentFilters, path?.elements]);

  const hasActiveFilters = activeFilters.length > 0;

  const recordBehaviorEvent = useCallback((payload) => {
    if (!sessionIdRef.current) {
      return Promise.resolve();
    }

    return recordLearningPathSessionEvent(id, sessionIdRef.current, {
      zoomLevel: Number(zoom.toFixed(2)),
      activeFiltersJson: activeFilters.length ? JSON.stringify(activeFilters) : null,
      ...payload,
    }).catch(() => {});
  }, [activeFilters, id, zoom]);

  const closeTrackedElement = useCallback(() => {
    if (!openElementRef.current) {
      return;
    }

    const trackedElement = openElementRef.current;
    openElementRef.current = null;

    void recordBehaviorEvent({
      eventType: 'element-close',
      pathElementId: trackedElement.elementId,
      durationMs: Math.max(0, Date.now() - trackedElement.startedAt),
    });
  }, [recordBehaviorEvent]);

  const finalizePendingExternalResource = useCallback(() => {
    if (!pendingExternalResourceRef.current) {
      return;
    }

    const pendingResource = pendingExternalResourceRef.current;

    if (pendingResource.windowRef && !pendingResource.windowRef.closed) {
      return;
    }

    if (pendingResource.pollId) {
      window.clearInterval(pendingResource.pollId);
    }

    pendingExternalResourceRef.current = null;

    void recordBehaviorEvent({
      eventType: 'resource-close',
      pathElementId: pendingResource.elementId,
      resourceType: pendingResource.resourceType,
      durationMs: Math.max(0, Date.now() - pendingResource.startedAt),
    });
  }, [recordBehaviorEvent]);

  const forceFinalizePendingExternalResource = useCallback(() => {
    if (!pendingExternalResourceRef.current) {
      return;
    }

    const pendingResource = pendingExternalResourceRef.current;
    if (pendingResource.pollId) {
      window.clearInterval(pendingResource.pollId);
    }

    pendingExternalResourceRef.current = null;

    void recordBehaviorEvent({
      eventType: 'resource-close',
      pathElementId: pendingResource.elementId,
      resourceType: pendingResource.resourceType,
      durationMs: Math.max(0, Date.now() - pendingResource.startedAt),
    });
  }, [recordBehaviorEvent]);

  const stopMediaTracking = useCallback(() => {
    if (mediaTransferRef.current || !activeMediaRef.current) {
      return;
    }

    const currentMedia = activeMediaRef.current;
    activeMediaRef.current = null;

    void recordBehaviorEvent({
      eventType: 'resource-close',
      pathElementId: currentMedia.elementId,
      resourceType: currentMedia.resourceType,
      durationMs: Math.max(0, Date.now() - currentMedia.startedAt),
    });
  }, [recordBehaviorEvent]);

  const startMediaTracking = useCallback((element, mediaType) => {
    if (!element) {
      return;
    }

    const currentMedia = activeMediaRef.current;
    if (currentMedia && currentMedia.elementId === element.id && currentMedia.resourceType === mediaType) {
      mediaTransferRef.current = false;
      return;
    }

    if (currentMedia) {
      stopMediaTracking();
    }

    activeMediaRef.current = {
      elementId: element.id,
      resourceType: mediaType,
      startedAt: Date.now(),
    };
    mediaTransferRef.current = false;

    void recordBehaviorEvent({
      eventType: 'resource-open',
      pathElementId: element.id,
      resourceType: mediaType,
    });
  }, [recordBehaviorEvent, stopMediaTracking]);

  const getClickPosition = useCallback((event) => {
    if (!event || !playPageRef.current) {
      return null;
    }

    if (typeof event.clientX !== 'number' || typeof event.clientY !== 'number') {
      return null;
    }

    const rect = playPageRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return null;
    }

    const xPercent = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
    const yPercent = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));

    return {
      clickXPercent: Number(xPercent.toFixed(2)),
      clickYPercent: Number(yPercent.toFixed(2)),
    };
  }, []);

  const trackElementOpen = useCallback((element, clickPosition = null) => {
    if (!element) {
      return;
    }

    closeTrackedElement();

    sequenceIndexRef.current += 1;
    openElementRef.current = {
      elementId: element.id,
      startedAt: Date.now(),
    };

    const previousPathElementId = lastOpenedElementIdRef.current;
    lastOpenedElementIdRef.current = element.id;

    void recordBehaviorEvent({
      eventType: 'element-open',
      pathElementId: element.id,
      previousPathElementId,
      sequenceIndex: sequenceIndexRef.current,
      clickXPercent: clickPosition?.clickXPercent,
      clickYPercent: clickPosition?.clickYPercent,
    });
  }, [closeTrackedElement, recordBehaviorEvent]);

  useEffect(() => {
    const handleFocus = () => {
      finalizePendingExternalResource();
    };

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        finalizePendingExternalResource();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [finalizePendingExternalResource]);

  useEffect(() => () => {
    forceFinalizePendingExternalResource();
    closeTrackedElement();
    mediaTransferRef.current = false;
    stopMediaTracking();

    if (sessionIdRef.current) {
      endLearningPathSession(id, sessionIdRef.current, {
        lastPathElementId: lastOpenedElementIdRef.current,
      }).catch(() => {});
      sessionIdRef.current = null;
    }
  }, [closeTrackedElement, forceFinalizePendingExternalResource, id, stopMediaTracking]);

  const filterMenuItems = useMemo(() => contentFilters.map((filter) => ({
    key: filter.key,
    disabled: (filterCounts[filter.key] || 0) === 0,
    label: (
      <div className={styles.playFilterMenuItem}>
        <span className={styles.playFilterMenuLabel}>
          {filter.icon}
          <span>{filter.label}</span>
        </span>
        <Badge count={filterCounts[filter.key] || 0} size="small" />
      </div>
    ),
  })), [contentFilters, filterCounts]);

  const elementMatchesActiveFilters = useCallback((element) => {
    if (!activeFilters.length) {
      return true;
    }

    return contentFilters.some((filter) => activeFilters.includes(filter.key) && filter.matches(element));
  }, [activeFilters, contentFilters]);

  const toggleFilter = useCallback((filterKey) => {
    setActiveFilters((currentFilters) => (
      currentFilters.includes(filterKey)
        ? currentFilters.filter((currentFilter) => currentFilter !== filterKey)
        : [...currentFilters, filterKey]
    ));
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters([]);
  }, []);

  const changeZoom = useCallback((delta) => {
    setZoom((currentZoom) => Math.max(minZoom, Math.min(maxZoom, Math.round((currentZoom + delta) * 10) / 10)));
  }, [maxZoom, minZoom]);

  const filterButtonLabel = hasActiveFilters
    ? t('learningPathPages.play.filterElementsActive', 'Filter elements ({{count}})', { count: activeFilters.length })
    : t('learningPathPages.play.filterElements', 'Filter elements');

  const recordCountInteraction = useCallback((element, interactionType) => {
    recordLearningPathElementInteraction(id, element.id, interactionType).catch(() => {});
  }, [id]);

  const openTrackedWindow = useCallback((href) => {
    const popup = window.open(href, '_blank');
    if (popup) {
      try {
        popup.opener = null;
      } catch (error) {
        // Ignore opener hardening failures across origins.
      }
    }

    return popup;
  }, []);

  const trackExternalResource = useCallback((element, resourceType, href) => {
    if (!element || !resourceType || !href) {
      return;
    }

    forceFinalizePendingExternalResource();

    void recordBehaviorEvent({
      eventType: 'resource-open',
      pathElementId: element.id,
      resourceType,
    });

    const windowRef = openTrackedWindow(href);
    const pendingResource = {
      elementId: element.id,
      resourceType,
      startedAt: Date.now(),
      windowRef,
      pollId: null,
    };

    if (windowRef) {
      pendingResource.pollId = window.setInterval(() => {
        if (windowRef.closed) {
          finalizePendingExternalResource();
        }
      }, 1000);
    }

    pendingExternalResourceRef.current = pendingResource;
  }, [finalizePendingExternalResource, forceFinalizePendingExternalResource, openTrackedWindow, recordBehaviorEvent]);

  const launchElementAction = useCallback((element, action) => {
    if (!element || !action) {
      return;
    }

    recordCountInteraction(element, action.interactionType);
    trackExternalResource(element, interactionTypeToResourceType[action.interactionType], action.getHref(element));
  }, [recordCountInteraction, trackExternalResource]);

  const openElement = useCallback((element, event) => {
    trackElementOpen(element, getClickPosition(event));
    recordCountInteraction(element, 'element-click');

    const directOpenAction = getDirectOpenAction(element);
    if (directOpenAction) {
      launchElementAction(element, directOpenAction);
      closeTrackedElement();
      return;
    }

    setSelectedElement(element);
  }, [closeTrackedElement, getClickPosition, getDirectOpenAction, launchElementAction, recordCountInteraction, trackElementOpen]);

  const handleActionOpen = useCallback((event, action, element) => {
    event.preventDefault();
    launchElementAction(element, action);
  }, [launchElementAction]);

  const pauseOtherPlayers = useCallback((activePlayer) => {
    const players = [
      { key: 'drawer-audio', ref: audioRef },
      { key: 'drawer-video', ref: videoRef },
      { key: 'mini-audio', ref: minimizedAudioRef },
      { key: 'mini-video', ref: minimizedVideoRef },
    ];

    players.forEach(({ key, ref }) => {
      if (key !== activePlayer && ref.current && !ref.current.paused) {
        ref.current.pause();
      }
    });
  }, []);

  const getMediaSnapshot = useCallback((element) => {
    if (!element) return null;

    if (videoRef.current && !videoRef.current.paused && element.video) {
      return {
        type: 'video',
        title: element.name,
        elementId: element.id,
        src: getFileUrl(element.video.relativeUrl) || `/api/files/${element.video.id}/download`,
        currentTime: videoRef.current.currentTime || 0,
      };
    }

    if (audioRef.current && !audioRef.current.paused && element.audio) {
      return {
        type: 'audio',
        title: element.name,
        elementId: element.id,
        src: getFileUrl(element.audio.relativeUrl) || `/api/files/${element.audio.id}/download`,
        currentTime: audioRef.current.currentTime || 0,
      };
    }

    return null;
  }, []);

  const handleMediaPlay = (mediaType) => {
    const element = selectedElement;
    if (!element) return;

    pauseOtherPlayers(mediaType === 'audio' ? 'drawer-audio' : 'drawer-video');

    const interactionType = mediaType === 'audio' ? 'audio-open' : 'video-open';
    const interactionKey = `${element.id}-${interactionType}`;
    if (!loggedMediaInteractionsRef.current[interactionKey]) {
      loggedMediaInteractionsRef.current[interactionKey] = true;
      recordCountInteraction(element, interactionType);
    }

    startMediaTracking(element, mediaType);
  };

  const handleMinimizedMediaPlay = () => {
    if (!minimizedMedia) return;

    pauseOtherPlayers(minimizedMedia.type === 'audio' ? 'mini-audio' : 'mini-video');

    const interactionType = minimizedMedia.type === 'audio' ? 'audio-open' : 'video-open';
    const interactionKey = `${minimizedMedia.elementId}-${interactionType}`;
    if (!loggedMediaInteractionsRef.current[interactionKey]) {
      loggedMediaInteractionsRef.current[interactionKey] = true;
      recordCountInteraction({ id: minimizedMedia.elementId }, interactionType);
    }

    startMediaTracking({ id: minimizedMedia.elementId }, minimizedMedia.type);
  };

  const handleMediaPause = () => {
    stopMediaTracking();
  };

  const handleDrawerClose = () => {
    const snapshot = getMediaSnapshot(selectedElement);
    if (snapshot) {
      mediaTransferRef.current = true;
      setMinimizedMedia(snapshot);
    }

    closeTrackedElement();
    setSelectedElement(null);
  };

  const handleMinimizedMediaLoaded = () => {
    const player = minimizedMedia?.type === 'audio' ? minimizedAudioRef.current : minimizedVideoRef.current;
    if (!player || !minimizedMedia) return;

    if (Math.abs((player.currentTime || 0) - minimizedMedia.currentTime) > 0.5) {
      player.currentTime = minimizedMedia.currentTime;
    }

    void player.play().catch(() => {});
  };

  const handleDrawerMediaLoaded = (mediaType) => {
    const pendingRestore = pendingRestoreRef.current;
    const player = mediaType === 'audio' ? audioRef.current : videoRef.current;

    if (!pendingRestore || pendingRestore.type !== mediaType || !player) {
      return;
    }

    if (Math.abs((player.currentTime || 0) - pendingRestore.currentTime) > 0.5) {
      player.currentTime = pendingRestore.currentTime;
    }

    pendingRestoreRef.current = null;
    void player.play().catch(() => {});
  };

  const enlargeMinimizedMedia = () => {
    if (!minimizedMedia || !path?.elements?.length) return;

    const currentTime = minimizedMedia.type === 'audio'
      ? (minimizedAudioRef.current?.currentTime || minimizedMedia.currentTime || 0)
      : (minimizedVideoRef.current?.currentTime || minimizedMedia.currentTime || 0);

    const element = path.elements.find((item) => item.id === minimizedMedia.elementId);
    if (!element) return;

    mediaTransferRef.current = true;

    if (minimizedAudioRef.current) {
      minimizedAudioRef.current.pause();
    }

    if (minimizedVideoRef.current) {
      minimizedVideoRef.current.pause();
    }

    pendingRestoreRef.current = {
      type: minimizedMedia.type,
      currentTime,
    };

    setMinimizedMedia(null);
    trackElementOpen(element);
    setSelectedElement(element);
  };

  const closeMinimizedMedia = () => {
    mediaTransferRef.current = false;
    if (minimizedAudioRef.current) {
      minimizedAudioRef.current.pause();
    }

    if (minimizedVideoRef.current) {
      minimizedVideoRef.current.pause();
    }

    setMinimizedMedia(null);
  };

  const selectedElementActions = selectedElement ? getElementActionItems(selectedElement) : [];

  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.playShell}><Spin /></div>
      </PagesWrapper>
    );
  }

  if (error) {
    return (
      <PagesWrapper>
        <div className={styles.playShell}><Alert type="error" message={t('learningPathPages.play.loadError', 'Learning path not found or not published.')} /></div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.playShell}>
        <div className={`${styles.pageHeader} ${styles.playHeaderRow}`}>
          <div className={styles.playTitleWrap}>
            <h1 className={styles.pageTitle}>{path.name}</h1>
          </div>
          <div className={styles.playToolbar}>
            <Space size={8} align="center">
              <Space.Compact>
                <Button
                  className={styles.playZoomButton}
                  onClick={() => changeZoom(-0.1)}
                  disabled={zoom <= minZoom}
                  aria-label={t('learningPathPages.play.zoomOut', 'Zoom out')}
                >
                  -
                </Button>
                <Button
                  className={`${styles.playZoomButton} ${styles.playZoomButtonPrimary}`}
                  onClick={() => changeZoom(0.1)}
                  disabled={zoom >= maxZoom}
                  aria-label={t('learningPathPages.play.zoomIn', 'Zoom in')}
                >
                  +
                </Button>
              </Space.Compact>

              <Dropdown
                trigger={["click"]}
                placement="bottomRight"
                menu={{
                  items: filterMenuItems,
                  selectable: true,
                  multiple: true,
                  selectedKeys: activeFilters,
                  onSelect: ({ key }) => toggleFilter(String(key)),
                  onDeselect: ({ key }) => toggleFilter(String(key)),
                }}
                dropdownRender={(menu) => <div className={styles.playFilterDropdown}>{menu}</div>}
              >
                <Badge count={activeFilters.length} size="small">
                  <Button
                    className={`${styles.playFilterButton} ${hasActiveFilters ? styles.playFilterButtonActive : ''}`}
                    icon={<FilterOutlined />}
                    onClick={(event) => event.preventDefault()}
                    aria-label={t('learningPathPages.play.highlight', 'Highlight elements')}
                  >
                    <span className={styles.playFilterButtonText}>{filterButtonLabel}</span>
                  </Button>
                </Badge>
              </Dropdown>
              {hasActiveFilters ? (
                <Button
                  className={styles.playFilterClearButton}
                  icon={<CloseOutlined />}
                  onClick={clearFilters}
                  aria-label={t('learningPathPages.play.clearFilters', 'Clear filters')}
                />
              ) : null}
            </Space>
          </div>
        </div>

        <div className={styles.playCanvasShell}>
          <div className={styles.playCanvasStage}>
            <div ref={playPageRef} className={`${styles.a4Page} ${styles.playA4Page}`} style={{ width: `${zoom * 100}%` }}>
              {path.backgroundImage ? (
                <img className={styles.backgroundImage} src={getFileUrl(path.backgroundImage.relativeUrl)} alt={path.backgroundImage.altText || path.name} />
              ) : (
                <div className={styles.backgroundPlaceholder}>{t('learningPathPages.play.noBackground', 'No background image available.')}</div>
              )}
              {(path.elements || []).map((element) => (
                (() => {
                  const isSelected = selectedElement?.id === element.id;
                  const isMatched = elementMatchesActiveFilters(element);
                  const buttonLabel = getElementButtonLabel(element);

                  return (
                    <button
                      key={element.id}
                      type="button"
                      className={[
                        styles.pathElement,
                        styles.playPathElement,
                        hasActiveFilters && isMatched ? styles.playPathElementMatched : '',
                        hasActiveFilters && !isMatched ? styles.playPathElementDimmed : '',
                        isSelected ? styles.playPathElementSelected : '',
                      ].filter(Boolean).join(' ')}
                      style={{
                        left: `${element.xPercent}%`,
                        top: `${element.yPercent}%`,
                        width: `${element.widthPercent}%`,
                        height: `${element.heightPercent}%`,
                      }}
                      onClick={(event) => openElement(element, event)}
                      aria-label={buttonLabel}
                      title={buttonLabel}
                    />
                  );
                })()
              ))}
            </div>
          </div>
        </div>

        <div className={styles.playDisclaimerDivider} />

        <div className={styles.playDisclaimer}>
          <img
            src={LIFELogo}
            alt="Co-funded by the European Union"
            className={styles.playDisclaimerImage}
          />
          <p className={styles.playDisclaimerText}>
            Co-funded by the European Union. Views and opinions expressed are however those of the
            author(s) only and do not necessarily reflect those of the European Union or CINEA.
            Neither the European Union nor the granting authority can be held responsible for them.
          </p>
        </div>
      </div>

      <Drawer
        title={selectedElement?.name}
        open={Boolean(selectedElement)}
        onClose={handleDrawerClose}
        width={620}
      >
        {selectedElement ? (
          <div className={styles.playDrawerContent}>
            <div className={styles.playDrawerHeader}>
              <div className={styles.playDrawerTitle}>{selectedElement.name}</div>
              <div className={styles.playDrawerHint}>{t('learningPathPages.play.drawerHint', 'Open the resources linked to this hotspot.')}</div>
            </div>

            {selectedElement.richTextContentInfo && (
              <div className={styles.richContent} dangerouslySetInnerHTML={{ __html: selectedElement.richTextContentInfo }} />
            )}

            {selectedElement.audio && (
              <div className={styles.playInlineSection}>
                <div className={styles.playInlineSectionLabel}>
                  <span className={styles.playInlineSectionIcon}><SoundOutlined /></span>
                  <span>{t('learningPathPages.elementContent.audio', 'Audio')}</span>
                </div>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio
                  ref={audioRef}
                  controls
                  preload="metadata"
                  src={getFileUrl(selectedElement.audio.relativeUrl) || `/api/files/${selectedElement.audio.id}/download`}
                  onLoadedMetadata={() => handleDrawerMediaLoaded('audio')}
                  onPlay={() => handleMediaPlay('audio')}
                  onPause={handleMediaPause}
                  onEnded={handleMediaPause}
                  style={{ width: '100%' }}
                />
              </div>
            )}

            {selectedElement.video && (
              <Card size="small" title={<span className={styles.playVideoTitle}><PlayCircleOutlined /> {t('learningPathPages.elementContent.video', 'Video')}</span>}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  ref={videoRef}
                  controls
                  preload="metadata"
                  src={getFileUrl(selectedElement.video.relativeUrl) || `/api/files/${selectedElement.video.id}/download`}
                  onLoadedMetadata={() => handleDrawerMediaLoaded('video')}
                  onPlay={() => handleMediaPlay('video')}
                  onPause={handleMediaPause}
                  onEnded={handleMediaPause}
                  style={{ width: '100%' }}
                />
              </Card>
            )}

            {selectedElementActions.length > 0 && (
              <div className={styles.playActionList}>
                {selectedElementActions.map((action) => (
                  <a
                    key={action.key}
                    className={styles.playActionLink}
                    href={action.getHref(selectedElement)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => handleActionOpen(event, action, selectedElement)}
                  >
                    <span className={styles.playActionIcon}>{action.icon}</span>
                    <span className={styles.playActionBody}>
                      <span className={styles.playActionLabel}>{action.label}</span>
                      <span className={styles.playActionText}>{action.getText(selectedElement)}</span>
                    </span>
                  </a>
                ))}
              </div>
            )}

            {!selectedElement.richTextContentInfo && !selectedElement.pdf && !selectedElement.audio && !selectedElement.video && !selectedElement.linkUrl && !selectedElement.quizCollectionId && !selectedElement.surveyId && !selectedElement.trainingProfileId && (
              <Empty description={t('learningPathPages.play.noContent', 'No content configured for this element yet.')} />
            )}
          </div>
        ) : null}
      </Drawer>

      {minimizedMedia ? (
        <div className={`${styles.miniMediaPlayer} ${minimizedMedia.type === 'video' ? styles.miniMediaPlayerVideo : styles.miniMediaPlayerAudio}`}>
          <div className={styles.miniMediaHeader}>
            <div className={styles.miniMediaTitleGroup}>
              <span className={styles.miniMediaType}>{minimizedMedia.type === 'audio' ? t('learningPathPages.elementContent.audio', 'Audio') : t('learningPathPages.elementContent.video', 'Video')}</span>
              <span className={styles.miniMediaTitle}>{minimizedMedia.title}</span>
                onPause={handleMediaPause}
                onEnded={handleMediaPause}
            </div>
            <div className={styles.miniMediaActions}>
              <Button
                type="text"
                size="small"
                icon={<ArrowsAltOutlined />}
                onClick={enlargeMinimizedMedia}
                aria-label={t('learningPathPages.enlarge', 'Enlarge')}
              />
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={closeMinimizedMedia} aria-label={t('learningPathPages.close', 'Close')} />
            </div>
                onPause={handleMediaPause}
                onEnded={handleMediaPause}
          </div>
          {minimizedMedia.type === 'audio' ? (
            <audio
              ref={minimizedAudioRef}
              controls
              preload="metadata"
              src={minimizedMedia.src}
              onLoadedMetadata={handleMinimizedMediaLoaded}
              onPlay={handleMinimizedMediaPlay}
              style={{ width: '100%' }}
            />
          ) : (
            <video
              ref={minimizedVideoRef}
              controls
              preload="metadata"
              src={minimizedMedia.src}
              onLoadedMetadata={handleMinimizedMediaLoaded}
              onPlay={handleMinimizedMediaPlay}
              className={styles.miniMediaVideo}
            />
          )}
        </div>
      ) : null}
    </PagesWrapper>
  );
};

export default LearningPathPlay;
